import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Collections.css";

/*
  Usage:
  - Put brand images and model images in public/images/
    e.g. /public/images/porsche.jpg (brand card)
          /public/images/porsche-911.jpg (model card)
  - The code generates image paths by slugifying brand+model.
  - If an image is missing the component will show a default image at /images/default-car.jpg
*/

const BRANDS = {
  Porsche: ["911", "Cayenne", "Macan", "Panamera", "Taycan"],
  BMW: ["3 Series", "5 Series", "X5", "i4", "iX", "M3", "M5"],
  Mercedes: ["A-Class", "C-Class", "E-Class", "S-Class", "G-Class", "AMG GT"],
  Audi: ["A3", "A4", "A6", "A8", "Q5", "Q7", "RS7", "R8", "e-tron GT"],
  Ferrari: ["F8 Tributo", "SF90 Stradale", "812 Superfast", "Roma", "Portofino"],
  "Aston Martin": ["DB11", "Vantage", "DBS Superleggera", "DBX"],
  Lamborghini: ["Aventador", "Huracán", "Revuelto", "Urus"],
  Mustang: ["Mustang GT", "Mach 1", "Shelby GT500", "Mach-E"],
};

// Universal variants for all models
const VARIANTS = ["Base", "Standard", "Sport", "Sport Plus", "Luxury", "Turbo", "Turbo Plus", "Limited"];

// Short details for each model
const MODEL_DETAILS = {
  // Porsche
  "911": { tagline: "Timeless sports-car legend.", details: "The 911 blends race-bred performance with daily usability. Multiple trims from Carrera to Turbo deliver outstanding handling." },
  "Cayenne": { tagline: "Sporty luxury SUV.", details: "Cayenne balances space with sports-car dynamics. Powerful V6/V8 and hybrid options." },
  "Macan": { tagline: "Compact performance SUV.", details: "Agile crossover with Porsche handling in a compact package." },
  "Panamera": { tagline: "Executive performance saloon.", details: "Gran Turismo comfort with sports-car power and optional hybrid drivetrains." },
  "Taycan": { tagline: "Electric performance pioneer.", details: "High-performance EV lineup with instant torque and advanced chassis tech." },

  // BMW
  "3 Series": { tagline: "Driver's sedan.", details: "Balanced dynamics and modern interiors — benchmark sport-sedan." },
  "5 Series": { tagline: "Executive luxury & tech.", details: "Comfort, tech and efficiency for business-class driving." },
  "X5": { tagline: "Luxury mid-size SUV.", details: "Spacious, premium and capable with performance M variants available." },
  "i4": { tagline: "Sporty electric gran coupe.", details: "EV driving dynamics with BMW handling characteristics." },
  "iX": { tagline: "Electric flagship SUV.", details: "Full-size EV with advanced tech and range." },
  "M3": { tagline: "High-performance sport sedan.", details: "Track-capable performance and razor-sharp handling." },
  "M5": { tagline: "Super-sedan performance.", details: "Tremendous power with everyday usability and tech." },

  // Mercedes
  "A-Class": { tagline: "Entry-level luxury.", details: "Compact, tech-forward Mercedes with premium ambition." },
  "C-Class": { tagline: "Compact executive sedan.", details: "Refined ride, premium cabin and AMG options." },
  "E-Class": { tagline: "Luxury saloon standard.", details: "Comfort and tech with powerful engines and mild-hybrid options." },
  "S-Class": { tagline: "Flagship luxury and tech.", details: "Leading comfort, safety and luxury innovations." },
  "G-Class": { tagline: "Iconic luxury off-roader.", details: "Boxy, luxurious and immensely capable, with AMG power." },
  "AMG GT": { tagline: "Mercedes sports coupe.", details: "Purpose-built performance car with track capability." },

  // Audi
  "A3": { tagline: "Compact premium hatch/sedan.", details: "Entry Audi with refined interiors and Quattro options." },
  "A4": { tagline: "Balanced executive sedan.", details: "Comfortable, tech-rich and efficient." },
  "A6": { tagline: "Executive luxury sedan.", details: "Comfort and tech rivaling larger competitors." },
  "A8": { tagline: "Luxury flagship.", details: "Top-level refinement and technology in Audi range." },
  "Q5": { tagline: "Premium crossover.", details: "Daily-friendly SUV with balanced ride and space." },
  "Q7": { tagline: "Three-row luxury SUV.", details: "Family-oriented with premium feel and tech." },
  "RS7": { tagline: "High-performance sportback.", details: "Supercar levels of performance in a practical body." },
  "R8": { tagline: "Mid-engined supercar.", details: "V10-powered track-capable Audi with exotic looks." },
  "e-tron GT": { tagline: "Electric GT performance.", details: "High-performance EV grand tourer from Audi." },

  // Ferrari
  "F8 Tributo": { tagline: "V8 mid-engined maestro.", details: "Lightweight, aerodynamic V8 supercar tuned for speed and handling." },
  "SF90 Stradale": { tagline: "Hybrid V8 supercar.", details: "Plug-in hybrid delivering extreme power and electrified performance." },
  "812 Superfast": { tagline: "V12 front-engined GT.", details: "Naturally aspirated V12 with brutal power and character." },
  "Roma": { tagline: "Elegant GT coupe.", details: "Sophisticated styling and sporty performance for grand touring." },
  "Portofino": { tagline: "Convertible GT.", details: "Open-top V8 GT with everyday usability and performance." },

  // Aston Martin
  "DB11": { tagline: "Grand tourer elegance.", details: "Refined V8/V12 GT delivering comfort and speed." },
  "Vantage": { tagline: "Driver-focused sports car.", details: "Light, sharp and powerful with bespoke finishes." },
  "DBS Superleggera": { tagline: "Ultra GT performance.", details: "V12 power with lightweight construction for blistering pace." },
  "DBX": { tagline: "Sporty luxury SUV.", details: "Aston's luxurious and dynamic SUV offering." },

  // Lamborghini
  "Aventador": { tagline: "V12 exotic flagship.", details: "Brutal V12 performance, radical styling and track focus." },
  "Huracán": { tagline: "V10 supercar.", details: "Agile, loud and pure driving thrill with multiple trims." },
  "Revuelto": { tagline: "Next-gen hybrid V12.", details: "Next chapter of Lamborghini with electrified performance." },
  "Urus": { tagline: "Super SUV.", details: "Lambo performance in an SUV package for daily usability." },

  // Mustang / Ford
  "Mustang GT": { tagline: "Classic V8 muscle.", details: "Iconic American performance with modern tech and power." },
  "Mach 1": { tagline: "Track-capable Mustang.", details: "Heritage-inspired performance with handling upgrades." },
  "Shelby GT500": { tagline: "Supercharged beast.", details: "Extreme powerpack Mustang for straight-line dominance." },
  "Mach-E": { tagline: "Electric performance SUV.", details: "Ford's electric performance SUV combining practicality & speed." },
};

const MODEL_IMAGE_FILES = {
  // Mustang
  "Mustang GT": "mustang-gt.webp",
  "Mach 1": "mustang-mach1.avif",
  "Shelby GT500": "mustang-gt500.webp",
  "Mach-E": "mustang-mache.jpeg",

  // Lamborghini
  Aventador: "lamborghini-aventador.webp",
  Huracán: "lamborghini-huracan.avif",
  Revuelto: "lamborghini-revuelto.avif",
  Urus: "lamborghini-urus.jpg",

  // Aston Martin
  DBX: "aston-martin-dbx.avif",
  DB11: "aston-martin-db11.jpg",
  Vantage: "aston-martin-vantage.jpg",
  "DBS Superleggera": "aston-martin-dbs.webp",

  // Ferrari
  "F8 Tributo": "ferrari-f8.jpg",
  "SF90 Stradale": "ferrari-sf90.avif",
  "812 Superfast": "ferrari-812.jpg",
  Roma: "ferrari-roma.webp",
  Portofino: "ferrari-portofino.avif",

  // Audi
  A3: "audi-a3.jpg",
  A4: "audi-a4.jpg",
  A6: "audi-a6.avif",
  A8: "audi-a8.avif",
  Q5: "audi-q5.avif",
  Q7: "audi-q7.avif",
  RS7: "audi-rs7.jpeg",
  R8: "audi-r8.jpeg",
  "e-tron GT": "audi-etron.webp",

  // Mercedes
  "A-Class": "mercedes-aclass.avif",
  "C-Class": "mercedes-cclass.jpg",
  "E-Class": "mercedes-eclass.jpg",
  "S-Class": "mercedes-sclass.avif",
  "G-Class": "mercedes-gclass.webp",
  "AMG GT": "mercedes-amggt.jpg",

  // BMW
  "3 Series": "bmw-3.jpeg",
  "5 Series": "bmw-5.jpg",
  X5: "bmw-x5.avif",
  i4: "bmw-i4.jpg",
  iX: "bmw-ix.jpg",
  M3: "bmw-m3.jpg",
  M5: "bmw-m5.avif",

  // Porsche
  "911": "porsche-911.avif",
  Cayenne: "porsche-cayenne.avif",
  Macan: "porsche-macan.jpg",
  Panamera: "porsche-panamera.avif",
  Taycan: "porsche-taycan.avif",
};

// helper: slugify for image filenames
function slug(str = "") {
  return String(str)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^\w\s-]/g, "") // remove accents and punctuation
    .trim()
    .replace(/\s+/g, "-");
}

export default function Collections() {
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState("");
  const navigate = useNavigate();

  // Restore state when coming back
  useEffect(() => {
    const savedState = localStorage.getItem("revoraCollectionsState");
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        setSelectedBrand(state.selectedBrand || null);
        setSelectedModel(state.selectedModel || null);
        setSelectedVariant(state.selectedVariant || "");
        localStorage.removeItem("revoraCollectionsState");
      } catch (e) {
        console.error("Error restoring collections state:", e);
      }
    }
  }, []);

  const goToCustomize = () => {
    // Save current Collections state before navigating
    localStorage.setItem("revoraCollectionsState", JSON.stringify({
      selectedBrand,
      selectedModel,
      selectedVariant
    }));

    // Use localStorage as primary method
    const prefill = {
      brand: selectedBrand,
      model: selectedModel,
      variant: selectedVariant
    };
    localStorage.setItem("revoraPrefillCustom", JSON.stringify(prefill));
    
    // Also use navigate state as backup
    navigate("/customize", {
      state: {
        brand: selectedBrand,
        model: selectedModel,
        variant: selectedVariant
      }
    });
  };

  const openBrand = (brand) => {
    setSelectedBrand(brand);
    setSelectedModel(null);
    setSelectedVariant("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openModel = (model) => {
    setSelectedModel(model);
    setSelectedVariant(VARIANTS[0]); // Use first variant from universal list
  };

  const closeModal = () => {
    setSelectedModel(null);
  };

  // helper to get proper image (jpg/jpeg/webp)
  const imageFor = (brand, model = "") => {
    // If model exists in mapping → return correct file
    if (model && MODEL_IMAGE_FILES[model]) {
      return `/images/${MODEL_IMAGE_FILES[model]}`;
    }

    // original brand logic untouched
    if (!window.__revoraImgCache) window.__revoraImgCache = {};

    const key = `${brand}-${model}`;
    if (window.__revoraImgCache[key]) return window.__revoraImgCache[key];

    const base = slug(brand);
    const mod = model ? `-${slug(model)}` : "";
    const fileBase = `/images/${base}${mod}`;
    const exts = [".jpg", ".jpeg", ".webp", ".png"];

    for (let ext of exts) {
      const path = `${fileBase}${ext}`;
      const img = new Image();
      img.src = path;

      img.onload = () => {
        window.__revoraImgCache[key] = path;
      };

      if (img.complete) {
        window.__revoraImgCache[key] = path;
        return path;
      }
    }

    return "/images/default-car.jpg";
  };

  const handleTestDrive = (mode) => {
    // Save current Collections state before navigating
    localStorage.setItem("revoraCollectionsState", JSON.stringify({
      selectedBrand,
      selectedModel,
      selectedVariant
    }));

    // set prefill for TestDrive page, then navigate
    const prefill = {
      brand: selectedBrand,
      model: selectedModel,
      variant: selectedVariant,
      mode: mode === "custom" ? "custom" : "direct",
    };
    localStorage.setItem("revoraPrefill", JSON.stringify(prefill));
    navigate(`/test-drive?mode=${mode === "custom" ? "custom" : "direct"}`);
  };

  return (
    <section className="collections-page">
      <div className="collections-inner">
        <h1 className="collections-title">Revora Collections</h1>

        {!selectedBrand ? (
          <>
            <p className="collections-sub">Explore our curated brands — tap a brand to view models.</p>

            <div className="brands-grid">
              {Object.keys(BRANDS).map((brand) => (
                <div key={brand} className="brand-card" onClick={() => openBrand(brand)}>
                  <img
                    src={imageFor(brand)}
                    alt={brand}
                    onError={(e) => (e.currentTarget.src = "/images/default-car.jpg")}
                  />
                  <div className="brand-overlay">
                    <h2 className="brand-name">{brand}</h2>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <button className="back-btn" onClick={() => setSelectedBrand(null)}>← Back to brands</button>
            <h2 className="models-title">{selectedBrand} Models</h2>

            <div className="models-grid">
              {BRANDS[selectedBrand].map((model) => (
                <div key={model} className="model-card" onClick={() => openModel(model)}>
                  <img
                    src={imageFor(selectedBrand, model)}
                    alt={model}
                    onError={(e) => (e.currentTarget.src = "/images/default-car.jpg")}
                  />
                  <div className="model-overlay">
                    <div className="model-meta">
                      <h3 className="model-name">{model}</h3>
                      <p className="model-tag">{MODEL_DETAILS[model]?.tagline || "Luxury redefined."}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {selectedModel && (
          <div className="model-modal">
            <div className="modal-card">
              <button className="modal-close-card" onClick={closeModal}>x</button>

              <div className="modal-top">
                <img
                  src={imageFor(selectedBrand, selectedModel)}
                  alt={selectedModel}
                  onError={(e) => (e.currentTarget.src = "/images/default-car.jpg")}
                />
                <div className="modal-head">
                  <h3 className="modal-model">{selectedModel}</h3>
                  <p className="modal-tag">{MODEL_DETAILS[selectedModel]?.tagline || ""}</p>
                  <p className="modal-brand">{selectedBrand}</p>
                </div>
              </div>

              <div className="modal-body">
                <p className="modal-details">{MODEL_DETAILS[selectedModel]?.details || "Full details coming soon."}</p>

                <div className="variants-row">
                  <label>Variant</label>
                  <select
                    value={selectedVariant}
                    onChange={(e) => setSelectedVariant(e.target.value)}
                  >
                    {VARIANTS.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>

                <div className="modal-actions">
                  <button className="btn btn-ghost" onClick={() => handleTestDrive("direct")}>Test Drive</button>
                  <button className="btn btn-primary" onClick={goToCustomize}>
                    Customize & Book
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}